import * as z from 'zod';
import { CheckActor, CheckInMode, CheckOutMode } from './ReservableProductCheckIn';
import { isoDateToAppDate, isoDateTimeToAppTime } from '../base-codecs';
import { EntityId, Quantity, Ören, LocalizedString, Tag } from '../base-schemas';
import { LockData } from './Lock';
import { ReservationSources } from './ReservationSource';
import { ReservationItemStatus } from './ReservationItemStatus';
import { ReservationItemPriceMode } from './ReservationItemPriceMode';
import { ReservationItemPaymentStatus } from './ReservationItemPaymentStatus';
import { ReservableProductReservationOptionFormMode, ReservableProductReservationOptionSelectionFamily } from './ReservableProduct';
/** Varför den senaste automatiska in- eller utcheckningen misslyckades, t.ex. `reservable_in_use` eller `access_grant_failed` */
export const AutomaticCheckFailure = z.object({
    failed_at: isoDateTimeToAppTime,
    code: z.string(),
    message: z.string()
});
export const ReservationItemResource = z.object({
    _tag: Tag('ReservationItemResource'),
    id: EntityId,
    customer_id: EntityId.nullable(),
    charge_id: EntityId.nullable(),
    quantity: Quantity,
    note: z.string().nullish(),
    price: Ören,
    total_price: z.number(),
    price_mode: ReservationItemPriceMode,
    is_manually_scheduled: z.boolean(),
    delivery_date_time: isoDateTimeToAppTime.nullable(),
    return_date_time: isoDateTimeToAppTime.nullable(),
    delivered_at: isoDateTimeToAppTime.nullable(),
    returned_at: isoDateTimeToAppTime.nullable(),
    /** Produktens incheckningssätt när reservationen bokades. Ändras inte när produkten ändras. */
    check_in_mode: CheckInMode.nullish(),
    /** Produktens utcheckningssätt när reservationen bokades. Ändras inte när produkten ändras. */
    check_out_mode: CheckOutMode.nullish(),
    checked_in_via: CheckActor.nullish(),
    /** När incheckningen registrerades; delivered_at är när gästen kom */
    checked_in_recorded_at: isoDateTimeToAppTime.nullish(),
    checked_out_via: CheckActor.nullish(),
    /** När utcheckningen registrerades; returned_at är när gästen lämnade */
    checked_out_recorded_at: isoDateTimeToAppTime.nullish(),
    automatic_check_in_failure: AutomaticCheckFailure.nullish(),
    automatic_check_out_failure: AutomaticCheckFailure.nullish(),
    cancelled_at: isoDateTimeToAppTime.nullable(),
    created_at: isoDateTimeToAppTime.nullable(),
    updated_at: isoDateTimeToAppTime.nullable(),
    status: ReservationItemStatus,
    access_code: z.string().nullable().nullish(),
    access_code_lock_title: z.string().nullable().nullish(),
    has_active_lock_grant: z.boolean(),
    /** Gästens incheckningssida. Saknas tills artikeln har fått en länk */
    checkin_url: z.string().nullish(),
    payment_status: ReservationItemPaymentStatus,
    reservation_option: z.object({
        id: EntityId,
        selection_family: ReservableProductReservationOptionSelectionFamily,
        form_mode: ReservableProductReservationOptionFormMode
    }).nullable(),
    reservable_product: z.object({
        id: EntityId,
        title: LocalizedString,
        is_individually_tracked: z.boolean().optional().default(false),
        thumbnail: z.string().nullish(),
        reservation_product_information: LocalizedString.nullish(),
        has_lock: z.boolean().optional().default(false),
        /** Låset som är kopplat till produkten, även innan någon kod har utfärdats */
        lock: LockData.nullish(),
        /** Vad produktens statiska kod öppnar, t.ex. "Cykelförrådet" */
        static_access_code_title: z.string().nullish()
    }),
    reservables: z.array(z.object({
        id: EntityId,
        identifier: z.string(),
        access_code: z.string().nullable().nullish(),
        access_code_lock_title: z.string().nullable().nullish(),
        has_lock: z.boolean().optional().default(false),
        /** Låset som är kopplat till objektet, även innan någon kod har utfärdats */
        lock: LockData.nullish(),
        is_manually_selected: z.boolean().optional().default(false)
    })),
    /**
     * Det här är kunden associerad med reservationen om det är
     * en fristående reservation, annars är det bokningens kund.
     */
    customer: z.object({
        id: EntityId,
        name: z.string(),
        initials: z.string(),
        class: z.string().nullish(),
        color: z.string(),
        email: z.string().nullable(),
        phone_number: z.string().nullable()
    }).nullable(),
    /**
     * Ifall reservationen är associerad med en bokning så ska
     * detta fält innehålla bokningen.
     */
    booking: z.object({
        id: EntityId,
        booking_number: z.string(),
        position_id: EntityId.nullable(),
        check_in: isoDateToAppDate.nullable(),
        check_out: isoDateToAppDate.nullable()
    }).nullish(),
    reservation: z.object({
        id: EntityId,
        source: ReservationSources,
        created_at: isoDateTimeToAppTime.nullable(),
        updated_at: isoDateTimeToAppTime.nullable(),
        draft_expires_at: isoDateTimeToAppTime.nullish(),
        confirmed_at: isoDateTimeToAppTime.nullish(),
        cancelled_at: isoDateTimeToAppTime.nullish()
    }).nullable()
});
